import { useCallback, useEffect, useRef, useState } from 'react';
import {
  removeRideFromList,
  subscribeAdminRideFeed,
  upsertMappedRide,
} from '../lib/ride-realtime';
import { supabase } from '../lib/supabase';

/**
 * Loads rides once, then keeps the list in sync via Supabase Realtime (smooth inserts/updates).
 */
export function useAdminRidesFeed({ enabled = true, fetchRides, acceptAppRide = () => true }) {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [liveConnected, setLiveConnected] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const fetchRef = useRef(fetchRides);
  fetchRef.current = fetchRides;

  const applyFetchResult = useCallback((result) => {
    setRides(result.rides || []);
    setError(result.error || '');
    setUpdatedAt(new Date());
  }, []);

  const refresh = useCallback(async ({ showSpinner = true } = {}) => {
    if (!fetchRef.current) return;
    if (showSpinner) setLoading(true);
    try {
      const result = await fetchRef.current();
      applyFetchResult(result);
    } catch (err) {
      setError(err?.message || 'Could not load rides.');
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [applyFetchResult]);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh({ showSpinner: true });
  }, [enabled, refresh, fetchRides]);

  useEffect(() => {
    if (!enabled || !supabase) return undefined;

    const unsubscribe = subscribeAdminRideFeed(supabase, {
      acceptAppRide,
      onSubscribed: () => setLiveConnected(true),
      onAppChange: (event) => {
        setLiveConnected(true);
        if (event.type === 'delete') {
          setRides((prev) => removeRideFromList(prev, 'app', event.id));
          setUpdatedAt(new Date());
          return;
        }
        setRides((prev) => upsertMappedRide(prev, event.ride));
        setUpdatedAt(new Date());
      },
      onWebChange: (event) => {
        setLiveConnected(true);
        setRides((prev) => upsertMappedRide(prev, event.ride));
        setUpdatedAt(new Date());
      },
      onWebDelete: (id) => {
        setLiveConnected(true);
        setRides((prev) => removeRideFromList(prev, 'web', id));
        setUpdatedAt(new Date());
      },
      onHealRefresh: () => {
        refresh({ showSpinner: false });
      },
    });

    return unsubscribe;
  }, [enabled, acceptAppRide, refresh]);

  return {
    rides,
    setRides,
    loading,
    error,
    setError,
    refresh,
    liveConnected,
    updatedAt,
  };
}
